import { PolicyEvaluation } from 'types/api/policies/policy'
import {
  ReliabilityCategorySummary,
  ReliabilityReport
} from 'types/api/policies/reliability-report'
import { ReliabilityTestRun } from 'types/api/reliability-tests/runs'

/**
 * Get PolicyEvaluations for only dependencies (as opposed to RM tests)
 */
export function getDependencyPolicyEvaluations(
  reliabilityReport: ReliabilityReport
) {
  return (
    Object.values(reliabilityReport?.reliability ?? [])
      // Grab all the policy evaluations
      .flatMap((summary: ReliabilityCategorySummary) => summary?.policyStates)
      // only the ones impacting dependencies
      .filter((evaluation: PolicyEvaluation) =>
        Boolean(evaluation.dependencyId)
      )
  )
}

/**
 * Make a set of test definitions by collecting PolicyEvaluations per dependencyId
 */
export function getPolicyEvaluationsByDependencyId(
  reliabilityReport: ReliabilityReport
) {
  return (
    getDependencyPolicyEvaluations(reliabilityReport)
      // gather evaluations by dependencyId
      .reduce(
        (
          evaluationsByDependency: {
            [dependencyId: string]: PolicyEvaluation[]
          },
          evaluation
        ) => {
          const lastEvaluationDependencyId = evaluation.dependencyId

          if (!(lastEvaluationDependencyId in evaluationsByDependency)) {
            evaluationsByDependency[lastEvaluationDependencyId] = []
          }
          evaluationsByDependency[lastEvaluationDependencyId].push(evaluation)

          evaluationsByDependency[lastEvaluationDependencyId].sort(
            (a, b) => (a.order || 0) - (b.order || 0)
          )
          return evaluationsByDependency
        },
        {}
      )
  )
}

export function getHistoryRunUrl({
  dependencyId,
  serviceId,
  testId
}: {
  dependencyId?: string | null
  serviceId: string
  testId: string
}) {
  return dependencyId
    ? `/services/${serviceId}/history/${testId}/${dependencyId}`
    : `/services/${serviceId}/history/${testId}`
}

/**
 * A service test is run on A) the service itself or B) a dependency of that service.
 * If `dependencyId` is included, the url will include it (B). Otherwise, it goes to (A).
 */
export function getStatusRunUrl({
  dependencyId,
  runNumber,
  serviceId,
  testId
}: {
  dependencyId?: string | null
  runNumber?: number
  serviceId: string
  testId: string
}) {
  const runSegment = runNumber ? `/run/${runNumber}` : ''

  return dependencyId
    ? `/services/${serviceId}/status/${testId}/${dependencyId}${runSegment}`
    : `/services/${serviceId}/status/${testId}${runSegment}`
}

export function getIsCurrentlyRunning(
  isServiceRunningATest?: boolean,
  runningReliabilityTest?: ReliabilityTestRun | null,
  reliabilityTestId?: string,
  dependencyId?: string | null
) {
  return (
    isServiceRunningATest &&
    runningReliabilityTest?.guid === reliabilityTestId &&
    runningReliabilityTest?.dependencyId === dependencyId
  )
}

export function getDependencyUrl(serviceId: string, dependencyId: string) {
  return `/services/${serviceId}/dependencies#${dependencyId}`
}
